import {
  getCurrentFacilityId,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { logEventToBigQuery } from "../../api/postCall/logEventToBigQueryNew";
import SingleTabWithLink, {
  SingleTabWithLinkProps,
} from "../../components/singleTabWithLink/singleTabWithLink";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { OrganizationType } from "../../enums/organizationTypes";
import { Features } from "../../enums/screenNames";
import {
  clearDataFromStorage,
  logOutUserFromFirebase,
} from "../../redux/slice/firebaseAuth/logOutThunk";
import { getAllProviderDetailsForClinic } from "../../redux/slice/providerSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import AnalyticsFilledIcon from "../../svg/analyticsFilledIcon";
import AnalyticsIcon from "../../svg/analyticsIcon";
import CallIconDefault from "../../svg/call-icon-default";
import ClaimsIcon from "../../svg/claims";
import ClaimsIconFilled from "../../svg/claimsIconFilled";
import ConfigIconNew from "../../svg/config-icon-new";
import DeliveryIconNew from "../../svg/delivery-icon-new";
import Exit from "../../svg/exit";
import { NAV_ICON_INACTIVE_STROKE } from "../../svg/navIconColors";
import PendingTab from "../../svg/pending-tab";
import PendingTabFilled from "../../svg/pending-tab-filled";
import RisaLogoWhite from "../../svg/risa-logo-white";
import StatusIconNew from "../../svg/status-icon-new";
import TasksCheckIcon from "../../svg/tasks-check-icon";
import TicketingIcon from "../../svg/ticketing-icon";
import WorklistIconNew from "../../svg/worklist-icon-new";
import WorklistIcon from "../../svg/worklistIcon";
import WorklistIconFilled from "../../svg/worklistIconFilled";
import { logError } from "../../utils/customLogger";
import { logButtonClickEvent, ParamName } from "../../utils/events";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import AppealIcon from "../../svg/appeal-icon";
import AppealIconFilled from "../../svg/appeal-icon-filled";

const HeaderVersion2 = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [tabsList, setTabsList] = useState<SingleTabWithLinkProps[]>([]);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: facilityDetails } = useSelector(
    (state: RootState) => state.facilitySlice,
  );

  const navigationType = useNavigationType();

  const orgId = getOrgIdForFetchExternalWorklist();

  const isExternal = useMemo(() => {
    const currentFacilityId = getCurrentFacilityId() || "";
    const facilityType = userLoggedInFacilityType(currentFacilityId);
    return facilityType === "external" ? true : false;
  }, [orgId]);

  useEffect(() => {
    const fetchFeature = () => {
      try {
        const storedFeature = getItemFromLocalStorage("features");
        if (storedFeature) {
          generateTabs(storedFeature);
          clearTimer();
        } else {
          setTimer(fetchFeature, 500);
        }
      } catch (error) {
        logError(error as Error, "Error fetching features");
        clearTimer();
      }
    };

    const setTimer = (callback: () => void, delay: number) => {
      clearTimer();
      timerRef.current = setTimeout(callback, delay);
    };

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (user) {
      fetchFeature();
    }

    return () => {
      clearTimer();
    };
  }, [user, facilityDetails, providerDetails]);

  useEffect(() => {
    if (providerDetails) {
      dispatch(
        getAllProviderDetailsForClinic(providerDetails.FacilityId || ""),
      );
    }
  }, [providerDetails, dispatch]);

  useEffect(() => {
    if (tabsList && tabsList.length > 0 && navigationType !== "POP") {
      navigate(`/${tabsList[0].id}/`);
    } else if (tabsList && tabsList.length > 0 && location.pathname === "/") {
      navigate(`/${tabsList[0].id}/`);
    }
  }, [tabsList]);

  const generateTabs = (storedFeature: string[]) => {
    let tabs;

    switch (true) {
      case storedFeature.includes(Features.NYCBS_PHARMA_EXTERNAL):
        tabs = [
          { id: "nycbs-pharma-pa-worklists", label: "PA Orders" },
          { id: "nycbs-pharma-pa-completed", label: "Completed" },
        ];

        break;

      case orgId === OrganizationType.NYCBS_PHARMACY_EXTERNAL:
        tabs = [
          {
            id: "nycbs-external/sftp-file-orders",
            label: "Non-Stat",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "nycbs-external/stat-orders",
            label: "Stat",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "external-analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
        ];

        break;

      case isExternal:
        tabs = [
          {
            id: "astera-oncology-external",
            label: "Oncology",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "external-analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
          {
            id: "external-support",
            label: "Support",
            defaultIcon: <TicketingIcon />,
            selectedIcon: <TicketingIcon />,
          },
        ];

        break;

      case orgId === OrganizationType.ASTERA &&
        storedFeature.includes(Features.PHARMA_PA):
        tabs = [
          {
            id: "pharma-pa-worklists",
            label: "Worklists",
            defaultIcon: (
              <WorklistIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <WorklistIconNew />,
          },
          {
            id: "pharma-oncology-submissions",
            label: "Oncology Submissions",
            defaultIcon: (
              <DeliveryIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <DeliveryIconNew />,
          },
          {
            id: "pharma-pa-calling-worklist",
            label: "Non-ePA",
            defaultIcon: (
              <CallIconDefault strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <CallIconDefault />,
          },
          {
            id: "pharma-pa-pending-worklist",
            label: "Pending",
            defaultIcon: <PendingTab strokeColor={NAV_ICON_INACTIVE_STROKE} />,
            selectedIcon: <PendingTabFilled />,
          },

          {
            id: "pharma-oncology-appeals",
            label: "Appeals",
            defaultIcon: (
              <AppealIcon strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <AppealIconFilled />,
          },
          {
            id: "pharma-pa-status-internal",
            label: "Status",
            defaultIcon: (
              <StatusIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <StatusIconNew />,
          },
        ];

        break;

      case storedFeature.includes(Features.PRIMARY_CARE):
        tabs = [
          // {
          //   id: "pharma-pa-worklists",
          //   label: "Worklists",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },
          // {
          //   id: "pharma-oncology-submissions",
          //   label: "Oncology Submissions",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },

          {
            id: "pharma-pa-primary-care",
            label: "Primary Care",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          // Tasks tab (Astera OncoEMR worklist) — Primary Care dashboard only.
          {
            id: "tasks",
            label: "Tasks",
            defaultIcon: (
              <TasksCheckIcon strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <TasksCheckIcon />,
          },
          {
            id: "pharma-pa-status-internal",
            label: "Status",
            defaultIcon: (
              <StatusIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <StatusIconNew />,
          },
        ];

        break;

      case storedFeature.includes(Features.PHARMA_PA) &&
        (orgId === OrganizationType.NYCBS_STATS ||
          orgId === OrganizationType.NYCBS_STATS_DEV):
        tabs = [
          {
            id: "pharma-pa-worklists",
            label: "Worklists",
            defaultIcon: (
              <WorklistIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <WorklistIconNew />,
          },
          {
            id: "pharma-pa-sftp-file",
            label: "Delivery",
            defaultIcon: (
              <DeliveryIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <DeliveryIconNew />,
          },
          {
            id: "pharma-pa-calling-worklist",
            label: "Non-ePA",
            defaultIcon: (
              <CallIconDefault strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <CallIconDefault />,
          },
          {
            id: "pharma-pa-pending-worklist",
            label: "Pending",
            defaultIcon: <PendingTab strokeColor={NAV_ICON_INACTIVE_STROKE} />,
            selectedIcon: <PendingTabFilled />,
          },
          // {
          //   id: "pharma-pa-primary-care",
          //   label: "Primary Care",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },
          {
            id: "pharma-pa-status-internal",
            label: "Status",
            defaultIcon: (
              <StatusIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <StatusIconNew />,
          },

          // {
          //   id: "pharma-pa-diff-data",
          //   label: "Validator",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },
        ];

        break;

      case storedFeature.includes(Features.PHARMA_PA):
        tabs = [
          {
            id: "pharma-pa-worklists",
            label: "Worklists",
            defaultIcon: (
              <WorklistIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <WorklistIconNew />,
          },
          {
            id: "pharma-pa-sftp-file",
            label: "Delivery",
            defaultIcon: (
              <DeliveryIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <DeliveryIconNew />,
          },
          {
            id: "pharma-pa-calling-worklist",
            label: "Non-ePA",
            defaultIcon: (
              <CallIconDefault strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <CallIconDefault />,
          },
         
          {
            id: "pharma-pa-pending-worklist",
            label: "Pending",
            defaultIcon: <PendingTab strokeColor={NAV_ICON_INACTIVE_STROKE} />,
            selectedIcon: <PendingTabFilled />,
          },
          {
            id: "pharma-oncology-appeals",
            label: "Appeals",
            defaultIcon: (
              <AppealIcon strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <AppealIconFilled />,
          },
          // {
          //   id: "pharma-pa-primary-care",
          //   label: "Primary Care",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },
          {
            id: "pharma-pa-status-internal",
            label: "Status",
            defaultIcon: (
              <StatusIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />
            ),
            selectedIcon: <StatusIconNew />,
          },

          // {
          //   id: "pharma-pa-diff-data",
          //   label: "Validator",
          //   defaultIcon: <WorklistIcon />,
          //   selectedIcon: <WorklistIconFilled />,
          // },
        ];

        break;

      case storedFeature.includes(Features.EXTERNAL_WORKLIST):
        tabs = [
          {
            id: "external-analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
          {
            id: "external-worklist",
            label: "Orders",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
        ];

        break;

      default:
        tabs = [
          {
            id: "worklists",
            label: "Worklists",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "claims-check",
            label: "Claims",
            defaultIcon: <ClaimsIcon />,
            selectedIcon: <ClaimsIconFilled />,
          },
          {
            id: "eligibilty-check",
            label: "Check EV",
            defaultIcon: <ClaimsIcon />,
            selectedIcon: <ClaimsIconFilled />,
          },
          {
            id: "analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
        ];

        break;
    }

    const userIsAdminOrSuper =
      isCurrentUserAdmin() || isCurrentUserSuperAdmin();

    if (userIsAdminOrSuper && !isExternal) {
      tabs.push({
        id: "configurations",
        label: "Config",
        defaultIcon: <ConfigIconNew strokeColor={NAV_ICON_INACTIVE_STROKE} />,
        selectedIcon: <ConfigIconNew />,
      });
    }

    setTabsList(tabs);
  };

  const logOutUser = async () => {
    await logEventToBigQuery({
      event_name: EventsName.LOGOUT,
      patient_id: "",
      user_id: user?.id,
      user_email: user?.email || "",
      order_id: "",
      org_id: orgId,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
      },
    });
    logButtonClickEvent(ParamName.LOGOUT);
    dispatch(logOutUserFromFirebase());
    clearDataFromStorage();
  };

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="header--logo">
        <RisaLogoWhite height="52" width="52" />
      </div>
      <div className="header--worklist_tabs--container flex flex-1 flex-col gap-5">
        {tabsList.map((tab) => (
          <SingleTabWithLink
            key={tab.id}
            id={tab.id}
            label={tab.label}
            defaultIcon={tab.defaultIcon}
            selectedIcon={tab.selectedIcon}
          />
        ))}
      </div>
      <div className="header--actions-container mb-6 flex flex-col items-center justify-center gap-6">
        <div
          className="header-container--actions--profile-pic h-6 w-6 hover:scale-105 hover:cursor-pointer"
          onClick={() => {
            logEventToBigQuery({
              event_name: EventsName.SETTINGS_CLICKED,
              patient_id: "",
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              order_id: "",
              org_id: orgId ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.SETTINGS,
              },
            });
            navigate("/settings");
          }}
        >
          <img
            src={providerDetails?.ProfilePicUrl || "/user.png"}
            alt="profile-pic"
            className="header-container--actions--profile-pic__image h-full w-full rounded-full bg-white object-cover"
          />
        </div>
        <div className="header-container--actions--profile-pic--badge hover:scale-105 hover:cursor-pointer">
          <Exit onClick={logOutUser} stroke="white" />
        </div>
      </div>
    </div>
  );
};

export default HeaderVersion2;
